import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
  Divider,
  Chip,
  Slider,
  Button
} from '@mui/material';
import {
  Add,
  Remove,
  Tune,
  SwapVert,
  ZoomIn,
  ZoomOut,
  PlayArrow
} from '@mui/icons-material';
import CommentBottomSheet from './CommentBottomSheet';
import BlogElementRenderer from './BlogElementRenderer';
import CenteredVideoPlayer from './CenteredVideoPlayer';
import { config } from '../config';


const MobileBlogPreview = ({ blogElements, onClose, onUpdateElementPadding, onUpdateElementSize, blogPostId, blogTitle, author, showProfile = true }) => {
  const [showControls, setShowControls] = useState(false);
  const [selectedElementIndex, setSelectedElementIndex] = useState(null);
  const [selectedElementSize, setSelectedElementSize] = useState(100);
  const [selectedElementPadding, setSelectedElementPadding] = useState({
    top: 1,
    bottom: 1,
    left: 2,
    right: 2
  });
  const [commentSheetOpen, setCommentSheetOpen] = useState(false);
  const containerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));




  // Handle element size adjustment
  const handleSizeChange = (elementIndex, newSize) => {
    setSelectedElementSize(newSize);
    if (onUpdateElementSize) {
      onUpdateElementSize(elementIndex, newSize);
    }
  };

  // Handle element padding adjustment
  const handlePaddingChange = (elementIndex, paddingType, newValue) => {
    setSelectedElementPadding(prev => ({
      ...prev,
      [paddingType]: newValue
    }));
    if (onUpdateElementPadding) {
      onUpdateElementPadding(elementIndex, paddingType, newValue);
    }
  };
  // Simple element rendering - no pagination logic
  const createDisplayPages = (elements) => {
    if (!elements || elements.length === 0) return [];

    // Just return all elements in a single array - no pagination
    return [elements];
  };

  const renderElement = (element, index) => {
    // Get padding values from element styles or use defaults
    const elementPadding = {
      top: element.styles?.paddingTop || element.styles?.top || element.styles?.py || 1,
      bottom: element.styles?.paddingBottom || element.styles?.bottom || element.styles?.py || 1,
      left: element.styles?.paddingLeft || element.styles?.left || element.styles?.px || 2,
      right: element.styles?.paddingRight || element.styles?.right || element.styles?.px || 2
    };

    // Get size from element styles or use default
    const elementSize = element.styles?.size || 100;

    switch (element.type) {
      case 'title':
        return element.content ? (
          <Box sx={{ position: 'relative' }}>
            {(() => {
              // Check if content contains HTML tags
              const hasHtmlTags = /<[^>]+>/.test(element.content);

              const commonStyles = {
                pt: 0,
                pb: 0,
                pl: 0,
                pr: 0,
                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                textAlign: element.styles?.textAlign || 'center',
                transform: `scale(${elementSize / 100})`,
                transformOrigin: 'top center',
                cursor: 'pointer',
                border: selectedElementIndex === index ? '2px solid #1976d2' : '2px solid transparent',
                borderRadius: 1,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '100%',
                '&:hover': {
                  border: '2px solid #1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                },
                '& *': {
                  maxWidth: '100%',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                },
                ...element.styles
              };

              if (hasHtmlTags) {
                return (
                  <Box
                    onClick={() => {
                      // Element controls disabled
                    }}
                    sx={commonStyles}
                    dangerouslySetInnerHTML={{ __html: element.content }}
                  />
                );
              }

              return (
                <Typography
                  variant="h4"
                  onClick={() => {
                    // Element controls disabled
                  }}
                  sx={{
                    ...commonStyles,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {element.content}
                </Typography>
              );
            })()}
            {/* Element Controls */}
            {/* Control Icon - Hidden */}
            {false && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                display: 'flex',
                gap: 0.5,
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 1,
                p: 0.5,
                opacity: showControls ? 1 : 0,
                transition: 'opacity 0.2s'
              }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedElementIndex(index);
                    setSelectedElementSize(elementSize);
                    setSelectedElementPadding(elementPadding);
                    setShowControls(true);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Tune fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        ) : null;

      case 'text':
        return element.content ? (
          <Box sx={{ position: 'relative' }}>
            {(() => {
              // Check if content contains HTML tags
              const hasHtmlTags = /<[^>]+>/.test(element.content);

              const commonStyles = {
                pt: `${elementPadding.top}rem`,
                pb: `${elementPadding.bottom}rem`,
                pl: `${elementPadding.left}rem`,
                pr: `${elementPadding.right}rem`,
                lineHeight: 1.6,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                textAlign: element.styles?.textAlign || 'left',
                transform: `scale(${elementSize / 100})`,
                transformOrigin: 'top center',
                cursor: 'pointer',
                border: selectedElementIndex === index ? '2px solid #1976d2' : '2px solid transparent',
                borderRadius: 1,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                maxWidth: '100%',
                '&:hover': {
                  border: '2px solid #1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                },
                '& *': {
                  maxWidth: '100%',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word'
                },
                ...element.styles
              };

              if (hasHtmlTags) {
                return (
                  <Box
                    onClick={() => {
                      // Element controls disabled
                    }}
                    sx={commonStyles}
                    dangerouslySetInnerHTML={{ __html: element.content }}
                  />
                );
              }

              return (
                <Typography
                  variant="body1"
                  onClick={() => {
                    // Element controls disabled
                  }}
                  sx={{
                    ...commonStyles,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {element.content}
                </Typography>
              );
            })()}
            {/* Element Controls */}
            {/* Control Icon - Hidden */}
            {false && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                display: 'flex',
                gap: 0.5,
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 1,
                p: 0.5,
                opacity: showControls ? 1 : 0,
                transition: 'opacity 0.2s'
              }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedElementIndex(index);
                    setSelectedElementSize(elementSize);
                    setSelectedElementPadding(elementPadding);
                    setShowControls(true);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Tune fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        ) : null;

      case 'video_with_button_overlay':
      case 'media':
        return (
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: '414px',
                maxWidth: '414px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
                maxHeight: '80vh',
                position: 'relative',
                mx: '-16px',
                pt: `${elementPadding.top * 16}px`,
                pb: `${elementPadding.bottom * 16}px`,
                px: 0,
                transform: `scale(${elementSize / 100})`,
                transformOrigin: 'top center',
                cursor: 'pointer',
                border: selectedElementIndex === index ? '2px solid #1976d2' : '2px solid transparent',
                borderRadius: 0,
                '&:hover': {
                  border: '2px solid #1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              <Box sx={{
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  backgroundColor: '#000',
                  borderRadius: element.styles?.borderRadius || '0px',
                  overflow: 'hidden',
                  // Padding hack for aspect ratio compatibility
                  pt: (() => {
                    const ratio = element.styles?.aspectRatio || '16/9';
                    const [w, h] = ratio.split('/').map(Number);
                    return w && h ? `${(h / w) * 100}%` : '56.25%';
                  })(),
                  minHeight: '120px'
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {(() => {
                      const looksLikeLink = (s) => typeof s === 'string' && s.length > 5 && s.includes('.') && s !== 'Click Here' && !s.includes(' ');
                      const src = looksLikeLink(element.videoFile) ? element.videoFile :
                        (looksLikeLink(element.mediaUrl) ? element.mediaUrl :
                          (looksLikeLink(element.videoSrc) ? element.videoSrc : null));

                      const isVideo = src?.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i);
                      const isYouTube = src?.includes('youtube.com') || src?.includes('youtu.be');

                      if (!src) {
                        return (
                          <Box sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            backgroundColor: '#111',
                            color: '#444'
                          }}>
                            <PlayArrow sx={{ fontSize: 40, opacity: 0.5 }} />
                            <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '10px' }}>
                              No media uploaded
                            </Typography>
                          </Box>
                        );
                      }

                      if (isVideo) {
                        return (
                          <CenteredVideoPlayer
                            src={src}
                            poster={element.thumbnailUrl}
                            style={{
                              width: '100%',
                              height: '100%',
                              display: 'block',
                              objectFit: element.styles?.objectFit || 'contain'
                            }}
                          />
                        );
                      } else if (isYouTube) {
                        const getYouTubeID = (url) => {
                          if (!url) return '';
                          const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
                          const match = url.match(regExp);
                          return (match && match[2].length === 11) ? match[2] : null;
                        };
                        const videoId = getYouTubeID(src);
                        const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : src;
                        return (
                          <iframe
                            src={embedUrl}
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            allowFullScreen
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none'
                            }}
                          />
                        );
                      } else {
                        return (
                          <img
                            src={src}
                            alt="Blog media"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: element.styles?.objectFit || 'contain',
                              display: 'block'
                            }}
                          />
                        );
                      }
                    })()}

                    {(element.type === 'video_with_button_overlay' || element.showOverlayButton) && (
                      (() => {
                        const ratio = element.styles?.aspectRatio || '16/9';
                        const [w, h] = ratio.split('/').map(Number);
                        const isVertical = w && h && h > w;

                        // Calculate dynamic bottom position based on aspect ratio to avoid covering the bottom black bar (60px)
                        let dynamicBottom = '24px';
                        if (isVertical) {
                          const ratioValue = h / w;
                          if (ratioValue > 2) {
                            dynamicBottom = '140px'; // 9:19 and taller
                          } else if (ratioValue > 1.5) {
                            dynamicBottom = '80px'; // 9:16
                          } else if (ratioValue > 1) {
                            dynamicBottom = '60px'; // Other vertical
                          }
                        }

                        return (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: dynamicBottom,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '55%',
                              zIndex: 100,
                              display: 'flex',
                              justifyContent: 'center'
                            }}
                          >
                            <Button
                              variant="contained"
                              href={element.url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                background: element.styles?.background || (element.styles?.backgroundColor || '#1976d2'),
                                color: element.styles?.color || '#ffffff',
                                borderRadius: element.styles?.borderRadius || '8px',
                                fontWeight: element.styles?.fontWeight || 'bold',
                                fontSize: '14px',
                                padding: '10px 20px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                textTransform: 'none',
                                width: '100%',
                                minHeight: '44px',
                                '&:hover': {
                                  opacity: 0.9,
                                  transform: 'scale(1.02)'
                                },
                                transition: 'all 0.2s ease-in-out'
                              }}
                            >
                              {element.content || 'Click Here'}
                            </Button>
                          </Box>
                        );
                      })()
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        );

      case 'button':
        return element.content ? (
          <Box sx={{
            mb: 2,
            textAlign: element.styles?.textAlign || 'center'
          }}>
            <Button
              variant="contained"
              href={element.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor: element.styles?.backgroundColor || '#1976d2',
                color: element.styles?.color || 'white',
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                fontWeight: element.styles?.fontWeight || 'bold',
                padding: element.styles?.padding || '8px 16px',
                borderRadius: element.styles?.borderRadius || '4px',
                textTransform: element.styles?.textTransform || 'none',
                transform: `scale(${elementSize / 100})`,
                transformOrigin: 'top center',
                '&:hover': {
                  backgroundColor: element.styles?.hoverBackgroundColor || element.styles?.backgroundColor || '#1565c0'
                },
                ...element.styles
              }}
            >
              {element.content}
            </Button>
          </Box>
        ) : null;

      case 'offer':
        return element.htmlContent ? (
          <div
            onClick={() => {
              // Element controls disabled
              // setSelectedElementIndex(index);
              // setSelectedElementSize(elementSize);
              // setSelectedElementPadding(elementPadding);
              // setShowControls(true);
            }}
            style={{
              width: '100%',
              border: process.env.NODE_ENV === 'development' ? '4px solid #2196F3' : 'none',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
              minHeight: '50px',
              padding: '16px',
              transform: `scale(${elementSize / 100})`,
              transformOrigin: 'top center',
              cursor: 'pointer'
            }}
            className="offer-element"
            dangerouslySetInnerHTML={{ __html: element.htmlContent }}
          />
        ) : null;

      case 'blackbar':
        return (
          <Box sx={{ position: 'relative' }}>
            <Box
              onClick={() => {
                // Element controls disabled
                // setSelectedElementIndex(index);
                // setSelectedElementSize(elementSize);
                // setSelectedElementPadding(elementPadding);
                // setShowControls(true);
              }}
              sx={{
                mb: 3,
                backgroundColor: element.styles?.backgroundColor || '#000000',
                height: element.styles?.height || '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: element.styles?.borderRadius || '0px',
                px: 3,
                transform: `scale(${elementSize / 100})`,
                transformOrigin: 'top center',
                cursor: 'pointer',
                border: selectedElementIndex === index ? '2px solid #1976d2' : '2px solid transparent',
                '&:hover': {
                  border: '2px solid #1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                },
                ...element.styles
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {(element.styles?.buttons || []).map((button, buttonIndex) => (
                  <Button
                    key={buttonIndex}
                    variant={button.variant || 'contained'}
                    sx={{
                      backgroundColor: button.backgroundColor || '#ffffff',
                      color: button.color || '#000000',
                      '&:hover': {
                        backgroundColor: button.hoverColor || '#f0f0f0'
                      }
                    }}
                  >
                    {button.text || 'Button'}
                  </Button>
                ))}
                {(element.styles?.buttons || []).length === 0 && (
                  <Typography variant="body2" color="white" sx={{ opacity: 0.7 }}>
                    Click to add buttons
                  </Typography>
                )}
              </Box>
            </Box>
            {/* Element Controls */}
            {/* Control Icon - Hidden */}
            {false && (
              <Box sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                display: 'flex',
                gap: 0.5,
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: 1,
                p: 0.5,
                opacity: showControls ? 1 : 0,
                transition: 'opacity 0.2s',
                zIndex: 1
              }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedElementIndex(index);
                    setSelectedElementSize(elementSize);
                    setSelectedElementPadding(elementPadding);
                    setShowControls(true);
                  }}
                  sx={{ p: 0.5 }}
                >
                  <Tune fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  if (blogElements.length === 0) {
    return (
      <Box sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <Typography variant="h6" color="text.secondary">
          No blog elements to preview
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#f5f5f5',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // Remove dotted borders from offer elements and ensure videos take full width (only in production)
        '& .offer-element': {
          '& *': process.env.NODE_ENV === 'production' ? {
            border: 'none !important',
            borderStyle: 'none !important',
            outline: 'none !important'
          } : {},
          // Ensure videos take full width and extend beyond padding
          '& video, & iframe[src*="youtube"], & iframe[src*="vimeo"], & iframe': {
            width: 'calc(100% + 32px) !important', // Extend beyond padding: 16px (16px * 2 = 32px)
            maxWidth: 'calc(100% + 32px) !important',
            height: 'auto !important',
            display: 'block !important',
            margin: '0 -16px !important', // Negative margin to counteract padding
            padding: '0 !important'
          },
          // Ensure video wrapper divs take full width and extend beyond padding (only for videos)
          '& div:has(> video) div[style*="position: relative"]': {
            width: 'calc(100% + 32px) !important',
            maxWidth: 'calc(100% + 32px) !important',
            margin: '0 -16px !important',
            paddingLeft: '0 !important',
            paddingRight: '0 !important'
          },
          // Ensure parent wrapper divs also take full width and extend beyond padding (only for videos)
          '& div:has(> video) div[style*="text-align: center"]': {
            width: 'calc(100% + 32px) !important',
            maxWidth: 'calc(100% + 32px) !important',
            margin: '0 -16px !important',
            paddingLeft: '0 !important',
            paddingRight: '0 !important'
          },
          // Target divs that contain videos specifically - apply to all ancestor divs
          '& div:has(> video)': {
            width: 'calc(100% + 32px) !important',
            maxWidth: 'calc(100% + 32px) !important',
            margin: '0 -16px !important',
            paddingLeft: '0 !important',
            paddingRight: '0 !important'
          },
          '& div:has(video)': {
            width: 'calc(100% + 32px) !important',
            maxWidth: 'calc(100% + 32px) !important',
            margin: '0 -16px !important',
            paddingLeft: '0 !important',
            paddingRight: '0 !important'
          }
        }
      }}
    >
      {/* Content Area - Fixed height without scroll */}
      <Box sx={{
        flex: 1,
        overflow: 'hidden',
        p: 0,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Simple element rendering - no pagination */}
        {blogElements.length === 0 ? (
          <Box sx={{
            textAlign: 'center',
            py: 8,
            color: 'text.secondary',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Typography variant="body1">
              No elements to display
            </Typography>
          </Box>
        ) : (
          <Box sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'white',
            p: 0, // No padding - let elements handle their own padding
            position: 'relative' // For absolute positioning of profile
          }}>
            {/* Affiliate Profile Image and Username - Show in preview to indicate space usage */}
            {showProfile && author && (author.profileIcon?.path || author.username || author.name) && (() => {
              const firstElement = blogElements.length > 0 ? blogElements[0] : null;
              const checkIsVideo = (el) => {
                if (!el) return false;
                if (el.type === 'video_with_button_overlay') return true;
                if (el.type !== 'media') return false;
                const src = el.videoFile || el.mediaUrl || el.videoSrc;
                if (typeof src !== 'string') return false;
                return src.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) || src.includes('youtube.com') || src.includes('youtu.be');
              };
              const isFirstElementVideo = checkIsVideo(firstElement);

              return (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    pointerEvents: 'none', // Allow clicks to pass through
                  }}
                >
                  {/* Profile Image */}
                  {author.profileIcon?.path && (
                    <Box
                      sx={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid #e0e0e0',
                        flexShrink: 0
                      }}
                    >
                      <img
                        src={
                          author.profileIcon.path.startsWith('http')
                            ? author.profileIcon.path
                            : `${config.SERVER_URL}${author.profileIcon.path}`
                        }
                        alt={author.username || author.name || 'Affiliate'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    </Box>
                  )}
                  {/* Username - use name as fallback */}
                  {(author.username || author.name) && (
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: isFirstElementVideo ? '#ffffff' : '#333',
                        textShadow: isFirstElementVideo ? '0 1px 3px rgba(0, 0, 0, 0.5)' : 'none'
                      }}
                    >
                      {author.username || author.name}
                    </Typography>
                  )}
                </Box>
              );
            })()}
            {/* Add padding to first element to make room for profile */}
            <Box sx={{
              paddingTop: (() => {
                if (!(showProfile && author)) return '0px';
                const firstElement = blogElements.length > 0 ? blogElements[0] : null;
                const checkIsVideo = (el) => {
                  if (!el) return false;
                  if (el.type === 'video_with_button_overlay') return true;
                  if (el.type !== 'media') return false;
                  const src = el.videoFile || el.mediaUrl || el.videoSrc;
                  if (typeof src !== 'string') return false;
                  return src.match(/\.(mp4|avi|mov|wmv|flv|webm)($|\?)/i) || src.includes('youtube.com') || src.includes('youtu.be');
                };
                if (checkIsVideo(firstElement)) return '0px';
                return author.profileIcon?.path ? '48px' : '30px';
              })()
            }}>
              {blogElements.map((element, index) => {
                const hasOverlay = element.type === 'video_with_button_overlay' || element.showOverlayButton;
                const ar = element.styles?.aspectRatio || '16/9';
                const [aw, ah] = ar.split('/').map(Number);
                const isTallVertical = aw && ah && (ah / aw) >= 1.5;
                return (
                  <BlogElementRenderer
                    key={element.id || index}
                    element={element}
                    index={index}
                    isMobile={true}
                    renderOverlayButton={!(hasOverlay && isTallVertical)}
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* Overlay Buttons - rendered at phone-frame level for tall vertical (9:16+) presets */}
      {blogElements.map((element, index) => {
        if (element.type !== 'video_with_button_overlay' && !element.showOverlayButton) return null;
        const ar = element.styles?.aspectRatio || '16/9';
        const [aw, ah] = ar.split('/').map(Number);
        const isTallVertical = aw && ah && (ah / aw) >= 1.5;
        if (!isTallVertical) return null;
        const align = element.styles?.textAlign || 'center';
        return (
          <Box
            key={`overlay-btn-${element.id || index}`}
            sx={{
              position: 'absolute',
              bottom: '70px',
              left: align === 'center' ? '50%' : (align === 'right' ? 'auto' : '24px'),
              right: align === 'right' ? '24px' : 'auto',
              transform: align === 'center' ? 'translateX(-50%)' : 'none',
              width: '55%',
              zIndex: 15,
              display: 'flex',
              justifyContent: align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start'),
            }}
          >
            <Button
              variant="contained"
              sx={{
                background: element.styles?.background || (element.styles?.backgroundColor || '#1976d2'),
                color: element.styles?.color || '#ffffff',
                borderRadius: element.styles?.borderRadius || '8px',
                fontWeight: element.styles?.fontWeight || 'bold',
                fontSize: element.styles?.fontSize || '16px',
                padding: element.styles?.padding || '12px 24px',
                boxShadow: element.styles?.boxShadow || '0 4px 12px rgba(0,0,0,0.3)',
                textTransform: element.styles?.textTransform || 'none',
                width: '100%',
                height: '44px',
                maxHeight: '44px',
                pointerEvents: 'none',
                '&:hover': {
                  opacity: 0.9,
                },
              }}
            >
              {element.content || 'Click Here'}
            </Button>
          </Box>
        );
      })}

      {/* Fixed Black Bar at Bottom */}
      <Box sx={{
        height: '60px',
        backgroundColor: '#000000',
        width: '100%',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 10,
        maxWidth: '414px', // Match mobile preview width
        px: 2
      }}>
        <Typography variant="body2" color="white" sx={{ opacity: 0.7 }}>
          Blog Preview
        </Typography>
        <Button
          variant="text"
          size="small"
          disabled={!blogPostId}
          sx={{
            backgroundColor: 'transparent',
            color: 'white',
            borderRadius: '50%',
            minWidth: '60px',
            width: '60px',
            height: '60px',
            padding: 0,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            },
            '&:disabled': {
              backgroundColor: 'transparent',
              color: '#999'
            }
          }}
          onClick={() => setCommentSheetOpen(true)}
        >
          <img
            src="/comments_icon.png"
            alt="Comment"
            style={{ width: '60px', height: '58px' }}
          />
        </Button>
      </Box>

      {/* Element Controls Panel */}
      {showControls && selectedElementIndex !== null && (
        <Box sx={{
          position: 'fixed',
          top: 100,
          right: 20,
          backgroundColor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          p: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          zIndex: 20,
          minWidth: 280,
          maxWidth: 320
        }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            Element Controls
          </Typography>

          {/* Size Control */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Size: {selectedElementSize}%
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => {
                  const newSize = Math.max(50, selectedElementSize - 10);
                  handleSizeChange(selectedElementIndex, newSize);
                }}
                sx={{ backgroundColor: '#f5f5f5' }}
              >
                <ZoomOut fontSize="small" />
              </IconButton>

              <Slider
                value={selectedElementSize}
                onChange={(e, newValue) => handleSizeChange(selectedElementIndex, newValue)}
                min={50}
                max={150}
                step={5}
                sx={{ width: 120 }}
              />

              <IconButton
                size="small"
                onClick={() => {
                  const newSize = Math.min(150, selectedElementSize + 10);
                  handleSizeChange(selectedElementIndex, newSize);
                }}
                sx={{ backgroundColor: '#f5f5f5' }}
              >
                <ZoomIn fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Padding Controls */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Padding (rem)
            </Typography>

            {['top', 'bottom', 'left', 'right'].map((paddingType) => {
              const currentValue = selectedElementPadding[paddingType];

              return (
                <Box key={paddingType} sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                    {paddingType.charAt(0).toUpperCase() + paddingType.slice(1)}: {currentValue}rem
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newValue = Math.max(0, currentValue - 0.5);
                        handlePaddingChange(selectedElementIndex, paddingType, newValue);
                      }}
                      sx={{ backgroundColor: '#f5f5f5', p: 0.5 }}
                    >
                      <Remove fontSize="small" />
                    </IconButton>

                    <Box sx={{
                      width: 60,
                      height: 4,
                      backgroundColor: '#e0e0e0',
                      borderRadius: 2,
                      position: 'relative'
                    }}>
                      <Box sx={{
                        width: `${Math.min(100, (currentValue / 5) * 100)}%`,
                        height: '100%',
                        backgroundColor: '#1976d2',
                        borderRadius: 2
                      }} />
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => {
                        const newValue = Math.min(5, currentValue + 0.5);
                        handlePaddingChange(selectedElementIndex, paddingType, newValue);
                      }}
                      sx={{ backgroundColor: '#f5f5f5', p: 0.5 }}
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>


          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Typography
              variant="caption"
              onClick={() => {
                setSelectedElementIndex(null);
                setShowControls(false);
              }}
              sx={{
                cursor: 'pointer',
                color: '#1976d2',
                textDecoration: 'underline'
              }}
            >
              Done
            </Typography>
          </Box>
        </Box>
      )}

      {/* Comment Bottom Sheet */}
      <CommentBottomSheet
        open={commentSheetOpen}
        onClose={() => setCommentSheetOpen(false)}
        blogPostId={blogPostId}
        blogTitle={blogTitle}
      />
    </Box>
  );
};

export default MobileBlogPreview;